import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { CANCELLATION_REASONS, CancellationReason } from "../../domain/types";

const COMMENT_LIMIT = 500;

interface CancelSubscriptionDialogProps {
    isOpen: boolean;
    title: string;
    /** What cancelling keeps, and until when. */
    message: string;
    isBusy?: boolean;
    onConfirm: (why: { reason?: CancellationReason; comment?: string }) => void;
    onClose: () => void;
}

/**
 * Cancelling, in one step.
 *
 * No discount is offered for staying. The cancellation asks for a reason and
 * a comment that are both optional: leaving never waits on a survey, and the
 * answers go to the provider's churn reports.
 */
export default function CancelSubscriptionDialog({
    isOpen,
    title,
    message,
    isBusy = false,
    onConfirm,
    onClose,
}: CancelSubscriptionDialogProps) {
    const { t } = useTranslation();
    const [reason, setReason] = useState<CancellationReason | "">("");
    const [comment, setComment] = useState("");

    // Every opening starts afresh.
    useEffect(() => {
        if (!isOpen) return;
        setReason("");
        setComment("");
    }, [isOpen]);

    return (
        <ConfirmDialog
            isOpen={isOpen}
            title={title}
            message={message}
            confirmLabel={t("subscription.billing.cancelConfirm.confirm")}
            destructive
            isBusy={isBusy}
            onConfirm={() =>
                onConfirm({
                    reason: reason || undefined,
                    comment: comment.trim() || undefined,
                })
            }
            onCancel={onClose}
        >
            <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1 text-sm text-content-secondary">
                    {t("subscription.billing.cancelFlow.reasonLabel")}
                    <select
                        value={reason}
                        onChange={(event) =>
                            setReason(event.target.value as CancellationReason | "")
                        }
                        className="rounded-lg border border-gray-200 bg-transparent px-3 py-2 text-sm text-content-primary dark:border-gray-700"
                    >
                        <option value="">
                            {t("subscription.billing.cancelFlow.reasonPlaceholder")}
                        </option>
                        {CANCELLATION_REASONS.map((option) => (
                            <option key={option} value={option}>
                                {t(`subscription.billing.cancelFlow.reasons.${option}`)}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-sm text-content-secondary">
                    {t("subscription.billing.cancelFlow.commentLabel")}
                    <textarea
                        value={comment}
                        maxLength={COMMENT_LIMIT}
                        rows={3}
                        onChange={(event) => setComment(event.target.value)}
                        className="rounded-lg border border-gray-200 bg-transparent px-3 py-2 text-sm text-content-primary dark:border-gray-700"
                    />
                </label>
            </div>
        </ConfirmDialog>
    );
}
